import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { Upload, X } from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { useCart } from "@/contexts/CartContext";
import { formatApiError } from "@/contexts/AuthContext";

const LEATHERS = [
  { id: "cowhide", label: "Cowhide", note: "Heritage weight" },
  { id: "sheepskin", label: "Sheepskin", note: "+$200 · supple drape" },
  { id: "goatskin", label: "Goatskin", note: "+$150 · matte hand" },
];

const COLORS = [
  { id: "obsidian", label: "Obsidian", hex: "#0b0b0b" },
  { id: "espresso", label: "Espresso", hex: "#3a2418" },
  { id: "cognac", label: "Cognac", hex: "#7a4a26" },
  { id: "ivory", label: "Ivory", hex: "#ddd2bd" },
  { id: "burgundy", label: "Burgundy", hex: "#5e1a24" },
  { id: "navy", label: "Navy", hex: "#111111" },
  { id: "olive", label: "Olive", hex: "#5b6238" },
  { id: "gold", label: "Brushed gold", hex: "#a88a3d" },
];

const ZIPS = [
  { id: "silver", label: "Brushed silver" },
  { id: "gold", label: "Aged gold" },
  { id: "black", label: "Onyx black" },
];

const STITCHES = [
  { id: "matching", label: "Matching" },
  { id: "contrast", label: "Contrast tonal" },
];

const PATCHES = [
  { id: "atelier-crest", label: "Atelier crest" },
  { id: "boscage-monogram", label: "Boscage monogram" },
  { id: "florence-1962", label: "Heritage · 1962" },
  { id: "lifetime-mark", label: "Lifetime mark" },
];

export default function PersonalizePage() {
  const { addToCart } = useCart();
  const navigate = useNavigate();
  const fileInput = useRef(null);

  const [leather, setLeather] = useState("cowhide");
  const [color, setColor] = useState("obsidian");
  const [zip, setZip] = useState("silver");
  const [stitch, setStitch] = useState("matching");
  const [embroidery, setEmbroidery] = useState("");
  const [patches, setPatches] = useState([]);
  const [m, setMeasurements] = useState({ chest: "", shoulder: "", sleeve: "", length: "" });
  const [referencePath, setReferencePath] = useState("");
  const [uploading, setUploading] = useState(false);
  const [price, setPrice] = useState(1200);
  const [adding, setAdding] = useState(false);

  const payload = useMemo(
    () => ({
      leather_type: leather,
      color,
      zip_style: zip,
      stitch_color: stitch,
      embroidery_text: embroidery.slice(0, 15),
      patches,
      chest: m.chest ? parseFloat(m.chest) : null,
      shoulder: m.shoulder ? parseFloat(m.shoulder) : null,
      sleeve: m.sleeve ? parseFloat(m.sleeve) : null,
      length: m.length ? parseFloat(m.length) : null,
      reference_image_path: referencePath || null,
    }),
    [leather, color, zip, stitch, embroidery, patches, m, referencePath],
  );

  useEffect(() => {
    const ctrl = new AbortController();
    api
      .post("/personalize/estimate", payload, { signal: ctrl.signal })
      .then((r) => setPrice(r.data.price))
      .catch(() => {});
    return () => ctrl.abort();
  }, [payload]);

  const togglePatch = (id) => {
    setPatches((curr) =>
      curr.includes(id) ? curr.filter((p) => p !== id) : [...curr, id],
    );
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("folder", "personalize-refs");
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setReferencePath(data.path);
      toast.success("Reference uploaded");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = "";
    }
  };

  const saveAndAdd = async () => {
    setAdding(true);
    try {
      const { data: saved } = await api.post("/personalize/save", payload);
      const colorObj = COLORS.find((c) => c.id === color);
      await addToCart({
        product_id: "personalize-bespoke",
        variant_id: `pz-${saved.config_id}`,
        size: "BESPOKE",
        quantity: 1,
        personalize_config_id: saved.config_id,
        title: `Bespoke ${LEATHERS.find((l) => l.id === leather).label} jacket`,
        image: "https://images.unsplash.com/photo-1610903524111-32ce642ed1eb",
        price: saved.price,
        is_preorder: true,
      });
      toast.success("Bespoke added · 14 days delivery");
      navigate("/cart");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setAdding(false);
    }
  };

  const activeColor = COLORS.find((c) => c.id === color);

  return (
    <div data-testid="personalize-page" className="bg-[#ffffff] min-h-screen">
      <section className="border-b border-[#000000]/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-16">
          <p className="overline">Bespoke programme</p>
          <h1 className="font-serif text-4xl md:text-6xl leading-tight mt-3">
            Personalize <span className="italic text-[#000000]">your jacket.</span>
          </h1>
          <p className="mt-5 max-w-2xl text-[#000000]/70 leading-relaxed">
            Cut to your measurements, hand-stitched by master craftspeople
            and delivered in 14 days. Real-time pricing updates as you choose.
          </p>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-14 grid lg:grid-cols-12 gap-12">
        {/* Live preview */}
        <div className="lg:col-span-5 lg:sticky lg:top-28 self-start">
          <div
            className="aspect-[3/4] relative overflow-hidden"
            style={{ background: activeColor.hex }}
            data-testid="personalize-preview"
          >
            <img
              src="https://images.unsplash.com/photo-1610903524111-32ce642ed1eb"
              alt="Preview"
              className="absolute inset-0 w-full h-full object-cover mix-blend-multiply opacity-70"
            />
            <div className="absolute inset-0 leather-grain" />
            <div className="absolute top-5 left-5 right-5 flex items-start justify-between">
              <span className="overline-light">Bespoke · No. PZ</span>
              <span
                className="inline-flex items-center gap-2 text-[10px] tracking-[0.22em] uppercase text-white bg-black/40 backdrop-blur-sm px-3 py-1"
              >
                <span className="w-3 h-3 border border-white/60" style={{ background: activeColor.hex }} />
                {activeColor.label}
              </span>
            </div>
            {embroidery && (
              <span
                data-testid="preview-embroidery"
                className="absolute bottom-6 left-6 font-serif italic text-2xl text-white drop-shadow-lg"
              >
                &ldquo;{embroidery}&rdquo;
              </span>
            )}
          </div>

          <div className="bg-[#ffffff] text-[#000000] p-6 mt-3 flex items-center justify-between">
            <div>
              <p className="overline-dark">Live price</p>
              <p
                data-testid="personalize-price"
                className="font-serif text-3xl mt-1"
              >
                ${price.toFixed(0)}
              </p>
            </div>
            <button
              data-testid="add-bespoke-button"
              onClick={saveAndAdd}
              disabled={adding}
              className="btn-primary"
            >
              {adding ? "Adding…" : "Add · pre-order"}
            </button>
          </div>
          <p className="text-[10px] tracking-[0.22em] uppercase text-[#000000]/45 mt-3">
            14-day pre-order tailoring · Cancellable within 48h
          </p>
        </div>

        {/* Configurator */}
        <div className="lg:col-span-7 space-y-12">
          {/* Leather */}
          <Group label="Leather">
            <div className="grid sm:grid-cols-3 gap-3" data-testid="leather-options">
              {LEATHERS.map((l) => (
                <Option
                  key={l.id}
                  testId={`leather-${l.id}`}
                  active={leather === l.id}
                  onClick={() => setLeather(l.id)}
                  title={l.label}
                  note={l.note}
                />
              ))}
            </div>
          </Group>

          {/* Color */}
          <Group label="Colour">
            <div className="grid grid-cols-4 sm:grid-cols-8 gap-3" data-testid="color-options">
              {COLORS.map((c) => (
                <button
                  key={c.id}
                  data-testid={`color-${c.id}`}
                  onClick={() => setColor(c.id)}
                  className={`relative aspect-square border ${
                    color === c.id
                      ? "border-[#000000]"
                      : "border-[#000000]/15 hover:border-[#000000]/40"
                  }`}
                  style={{ background: c.hex }}
                  title={c.label}
                  aria-label={c.label}
                >
                  {color === c.id && (
                    <span className="absolute inset-0 ring-1 ring-[#ffffff] ring-offset-2 ring-offset-[#000000]" />
                  )}
                </button>
              ))}
            </div>
            <p className="text-xs text-[#000000]/55 mt-3">{activeColor.label}</p>
          </Group>

          {/* Zip & stitch */}
          <div className="grid md:grid-cols-2 gap-10">
            <Group label="Zip">
              <div className="grid grid-cols-3 gap-3">
                {ZIPS.map((z) => (
                  <Option
                    key={z.id}
                    testId={`zip-${z.id}`}
                    active={zip === z.id}
                    onClick={() => setZip(z.id)}
                    title={z.label}
                  />
                ))}
              </div>
            </Group>
            <Group label="Stitch">
              <div className="grid grid-cols-2 gap-3">
                {STITCHES.map((s) => (
                  <Option
                    key={s.id}
                    testId={`stitch-${s.id}`}
                    active={stitch === s.id}
                    onClick={() => setStitch(s.id)}
                    title={s.label}
                  />
                ))}
              </div>
            </Group>
          </div>

          {/* Embroidery */}
          <Group label="Embroidery · 15 characters">
            <input
              data-testid="embroidery-input"
              maxLength={15}
              className="input-light bg-[#ffffff]"
              placeholder="e.g. Marco, 1962, or your initials"
              value={embroidery}
              onChange={(e) => setEmbroidery(e.target.value)}
            />
            <p className="text-[10px] uppercase tracking-[0.18em] text-[#000000]/40 mt-2">
              {embroidery.length}/15 · Adds $80 when filled
            </p>
          </Group>

          {/* Patches */}
          <Group label="Patches · multiple">
            <div className="grid sm:grid-cols-2 gap-3" data-testid="patch-options">
              {PATCHES.map((p) => (
                <Option
                  key={p.id}
                  testId={`patch-${p.id}`}
                  active={patches.includes(p.id)}
                  onClick={() => togglePatch(p.id)}
                  title={p.label}
                  note="+ $40"
                />
              ))}
            </div>
          </Group>

          {/* Measurements */}
          <Group label="Custom measurements (cm)">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="measurements-grid">
              {[
                ["chest", "Chest"],
                ["shoulder", "Shoulder"],
                ["sleeve", "Sleeve"],
                ["length", "Length"],
              ].map(([k, label]) => (
                <label key={k} className="block">
                  <span className="text-[10px] uppercase tracking-[0.2em] text-[#000000]/60">
                    {label}
                  </span>
                  <input
                    data-testid={`measure-${k}`}
                    inputMode="decimal"
                    className="input-light bg-[#ffffff] mt-2"
                    value={m[k]}
                    onChange={(e) =>
                      setMeasurements((s) => ({ ...s, [k]: e.target.value }))
                    }
                    placeholder="—"
                  />
                </label>
              ))}
            </div>
          </Group>

          {/* Reference */}
          <Group label="Reference image (optional)">
            <input
              ref={fileInput}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={onFile}
              data-testid="reference-file-input"
            />
            {!referencePath ? (
              <button
                data-testid="reference-upload-button"
                onClick={() => fileInput.current?.click()}
                disabled={uploading}
                className="w-full border border-dashed border-[#000000]/25 py-10 hover:border-[#000000] transition-colors flex flex-col items-center gap-3 text-[#000000]/70"
              >
                <Upload size={24} strokeWidth={1.5} />
                <span className="text-xs tracking-[0.22em] uppercase">
                  {uploading ? "Uploading…" : "Upload reference"}
                </span>
                <span className="text-[10px] text-[#000000]/40">JPG · PNG · WEBP</span>
              </button>
            ) : (
              <div className="relative inline-block">
                <img
                  src={fileUrl(referencePath)}
                  alt="Reference"
                  data-testid="reference-preview"
                  className="h-40 w-40 object-cover border border-[#000000]/15"
                />
                <button
                  onClick={() => setReferencePath("")}
                  className="absolute -top-2 -right-2 w-7 h-7 bg-[#ffffff] text-[#000000] flex items-center justify-center"
                  data-testid="reference-remove-button"
                  aria-label="Remove"
                >
                  <X size={14} />
                </button>
              </div>
            )}
          </Group>
        </div>
      </section>

      <Toaster theme="dark" position="top-center" />
    </div>
  );
}

const Group = ({ label, children }) => (
  <div>
    <h3 className="overline">{label}</h3>
    <div className="mt-4">{children}</div>
  </div>
);

const Option = ({ active, onClick, title, note, testId }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className={`text-left px-4 py-3 border transition-colors ${
      active
        ? "border-[#000000] text-[#000000]"
        : "border-[#000000]/15 text-[#000000]/80 hover:border-[#000000]/40"
    }`}
  >
    <span className="block text-sm tracking-wide">{title}</span>
    {note && (
      <span className="block text-[10px] uppercase tracking-[0.2em] text-[#000000]/45 mt-1">
        {note}
      </span>
    )}
  </button>
);
